
import { useState, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Toast from "./Toast";
import { CloseToastIcon, CompanyLogo, DropAndDragIcon, GoodToastIcon } from "../../../components/common/IconComponents";
import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";

const CompanyProfileTab = () => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files);
    }
  };

  const initialValues = {
    companyName: "",
    companyEmail: "",
    numberOfEmployees: "",
    country: "",
    state: "",
  };

  const dropDownOptions = [
    { key: "Select", value: "" },
    { key: "Nigeria", value: "option1" },
    { key: "Canada", value: "option2" },
    { key: "USA", value: "option3" },
    { key: "UK", value: "option4" },
    { key: "UAE", value: "option5" },
  ];

  const dropDownOptions2 = [
    { key: "Select", value: "" },
    { key: "Lagos", value: "option1" },
    { key: "Abuja", value: "option2" },
    { key: "Port Harcourt", value: "option3" },
    { key: "Ogun", value: "option4" },
    { key: "Delta", value: "option5" },
  ];

  const validationSchema = Yup.object({
    companyName: Yup.string().required("Required"),
    companyEmail: Yup.string(),
    numberOfEmployees: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
    state: Yup.string().required("Required"),
  });

  const toggleToast = () => {
    setShowToast(!showToast);
  };

  const handleFile = (files) => {
    const file = files[0];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "eqprvodt");

    axios
      .post("https://api.cloudinary.com/v1_1/darnus3nl/image/upload", formData)
      .then((response) => {
        const imageUrl = response.data.secure_url;
        setUploadedImage(imageUrl);
      })
      .catch((error) => {
        console.error("Error uploading image to Cloudinary: ", error);
      });
  };

  const onSubmit = (values, { resetForm }) => {
    console.log("Form data", values);
    setIsFormSubmitted(true);
    setCompanyLogo(uploadedImage); // Set the company logo to the uploaded image URL
    toggleToast();
    resetForm();
  };

  const handleToastClose = () => {
    setShowToast(false);
    setDragActive(false);
    setUploadedImage(null);
    setIsFormSubmitted(false);
  };

  return (
    <section>
      <div className="md:px-4">
        {showToast && (
          <Toast
            message="Your company’s profile has been successfully updated"
            successIcon={<GoodToastIcon className="w-4 h-4 lg:w-8 lg:h-8" />}
            closeIcon={<CloseToastIcon />}
            onClose={handleToastClose}
          />
        )}

        <nav className="md:w-[662px] sticky md:fixed z-20 top-16 md:top-0 bg-white">
          <h1 className="mt-4 py-2 text-lg md:text-[28px] font-medium">Company's Profile</h1>
        </nav>

        <div className="flex flex-col md:flex-row gap-8 mt-4 md:mt-24 mb-4">
          <span className="">
            {companyLogo ? (
              <img src={companyLogo} alt="Company Logo" className="rounded-full h-16 w-16" />
            ) : (
              <CompanyLogo className="rounded-full h-16 w-16" />
            )}
          </span>

          <div
            id="drag-drop-area"
            className={`flex items-center justify-center h-full border rounded bg-th-bg text-center p-4 ${dragActive ? "bg-white" : ""}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            {uploadedImage ? (
              <img src={uploadedImage} alt="Uploaded" className="max-h-64 max-w-full" />
            ) : (
              <div>
                <button
                  className="cursor-pointer border-none p-0.5 bg-transparent"
                  onClick={() => inputRef.current.click()}
                >
                  <DropAndDragIcon className="mb-1" />
                </button>
                <p className="mb-1 text-base font-normal">
                  <span className="text-enabled-btn">Click to upload</span> or drag and drop
                </p>
                <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            id="input-file-upload"
            multiple={false}
            onChange={handleChange}
            className="hidden"
          />
        </div>

        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <Form className="text-activesettings-text text-base font-normal">
                <FormikControl
                  control="input"
                  label="Company's name"
                  type="text"
                  placeholder="Flora"
                  name="companyName"
                />

                <FormikControl
                  className="w-full px-4 py-2 pr-10 text-disabled text-xs md:text-sm border border-disabled rounded-md focus:outline-none focus:ring-2 bg-neutral-bg shadow-md"
                  control="input"
                  label="Company's email address"
                  type="email"
                  placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
                  name="companyEmail"
                  disabled
                />

                <FormikControl
                  control="input"
                  label="Number of employees"
                  type="text"
                  placeholder="e.g. 50"
                  name="numberOfEmployees"
                />

                <FormikControl
                  control="select"
                  label="Country"
                  name="country"
                  options={dropDownOptions}
                />

                <FormikControl
                  control="select"
                  label="State"
                  name="state"
                  options={dropDownOptions2}
                />

                <button
                  type="submit"
                  className={`${isFormSubmitted ? "bg-gray-300 cursor-not-allowed" : "bg-enabled-btn"
                    } text-slate-50 text-base font-medium border rounded-md cursor-pointer my-8 py-2 px-20`}
                  disabled={!formik.isValid || isFormSubmitted}
                >
                  {isFormSubmitted ? "Save" : "Save"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default CompanyProfileTab;


// import { useState, useRef } from "react";
// import { Formik, Form } from "formik";
// import * as Yup from "yup";
// import axios from "axios";
// import Toast from "./Toast";
// import { CloseToastIcon, CompanyLogo, DropAndDragIcon, GoodToastIcon } from "../../../components/common/IconComponents";
// import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";

// const CompanyProfileTab = () => {
//   const [dragActive, setDragActive] = useState(false);
//   const [uploadedImage, setUploadedImage] = useState(null);
//   const [companyLogo, setCompanyLogo] = useState(null);
//   const [showToast, setShowToast] = useState(false);
//   const [isFormSubmitted, setIsFormSubmitted] = useState(false);
//   const inputRef = useRef(null);

//   const handleDrag = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     if (e.type === "dragenter" || e.type === "dragover") {
//       setDragActive(true);
//     } else if (e.type === "dragleave") {
//       setDragActive(false);
//     }
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setDragActive(false);
//     if (e.dataTransfer.files && e.dataTransfer.files[0]) {
//       handleFile(e.dataTransfer.files);
//     }
//   };

//   const handleChange = (e) => {
//     e.preventDefault();
//     if (e.target.files && e.target.files[0]) {
//       handleFile(e.target.files);
//     }
//   };

//   const initialValues = {
//     companyName: "",
//     companyEmail: "idowu.oluwatofunmi9@gmail.com",
//     numberOfEmployees: "",
//     country: "",
//     state: "",
//   };

//   const dropDownOptions = [
//     { key: "Select", value: "" },
//     { key: "Nigeria", value: "option1" },
//     { key: "Canada", value: "option2" },
//     { key: "USA", value: "option3" },
//     { key: "UK", value: "option4" },
//     { key: "UAE", value: "option5" },
//   ];

//   const dropDownOptions2 = [
//     { key: "Select", value: "" },
//     { key: "Lagos", value: "option1" },
//     { key: "Abuja", value: "option2" },
//     { key: "Port Harcourt", value: "option3" },
//     { key: "Ogun", value: "option4" },
//     { key: "Delta", value: "option5" },
//   ];

//   const validationSchema = Yup.object({
//     companyName: Yup.string().required("Required"),
//     companyEmail: Yup.string(),
//     numberOfEmployees: Yup.string().required("Required"),
//     country: Yup.string().required("Required"),
//     state: Yup.string().required("Required"),
//   });

//   const toggleToast = () => {
//     setShowToast(!showToast);
//   };

//   const handleFile = (files) => {
//     const file = files[0];
//     const formData = new FormData();
//     formData.append("file", file);
//     formData.append("upload_preset", "eqprvodt");

//     axios
//       .post("https://api.cloudinary.com/v1_1/darnus3nl/image/upload", formData)
//       .then((response) => {
//         const imageUrl = response.data.secure_url;
//         setUploadedImage(imageUrl);
//       })
//       .catch((error) => {
//         console.error("Error uploading image to Cloudinary: ", error);
//       });
//   };

//   const onSubmit = (values, { resetForm }) => {
//     console.log("Form data", values);
//     toggleToast();
//     setIsFormSubmitted(true);
//     setCompanyLogo(uploadedImage); // Set the company logo to the uploaded image URL
//     resetForm();
//   };

//   return (
//     <section>
//       <div className="md:px-4">
//         {showToast && (
//           <Toast
//             message="Your company’s profile has been successfully updated"
//             successIcon={<GoodToastIcon className="w-4 h-4 lg:w-8 lg:h-8" />}
//             closeIcon={<CloseToastIcon />}
//             onClose={toggleToast}
//           />
//         )}

//         <nav className="md:w-[662px] sticky md:fixed z-20 top-16 md:top-0 bg-white">
//           <h1 className="mt-4 py-2 text-xl md:text-[28px] font-medium">Company's Profile</h1>
//         </nav>

//         <div className="flex gap-8 mt-4 md:mt-24 mb-4">
//           <span className="">
//             {companyLogo ? (
//               <img src={companyLogo} alt="Company Logo" className="rounded-full h-16 w-16" />
//             ) : (
//                 <CompanyLogo className="rounded-full h-16 w-16" />
//             )}
//           </span>

//           <div
//             id="drag-drop-area"
//             className={`flex items-center justify-center h-full border rounded bg-th-bg text-center p-4 ${dragActive ? "bg-white" : ""}`}
//             onDragEnter={handleDrag}
//             onDragOver={handleDrag}
//             onDragLeave={handleDrag}
//             onDrop={handleDrop}
//           >
//             {uploadedImage ? (
//               <img src={uploadedImage} alt="Uploaded" className="max-h-64 max-w-full" />
//             ) : (
//               <div>
//                 <button
//                   className="cursor-pointer border-none p-0.5 bg-transparent"
//                   onClick={() => inputRef.current.click()}
//                 >
//                   <DropAndDragIcon className="mb-1" />
//                 </button>
//                 <p className="mb-1 text-base font-normal">
//                   <span className="text-enabled-btn">Click to upload</span> or drag and drop
//                 </p>
//                 <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
//               </div>
//             )}
//           </div>
//           <input
//             ref={inputRef}
//             type="file"
//             id="input-file-upload"
//             multiple={false}
//             onChange={handleChange}
//             className="hidden"
//           />
//         </div>

//         <div>
//           <Formik
//             initialValues={initialValues}
//             validationSchema={validationSchema}
//             onSubmit={onSubmit}
//           >
//             {(formik) => (
//               <Form className="text-activesettings-text text-base font-normal">
//                 <FormikControl
//                   control="input"
//                   label="Company's name"
//                   type="text"
//                   placeholder="Flora"
//                   name="companyName"
//                 />

//                 <FormikControl
//                   control="input"
//                   label="Company's email address"
//                   type="email"
//                   placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
//                   name="companyEmail"
//                   disabled
//                 />

//                 <FormikControl
//                   control="input"
//                   label="Number of employees"
//                   type="text"
//                   placeholder="e.g. 50"
//                   name="numberOfEmployees"
//                 />

//                 <FormikControl
//                   control="select"
//                   label="Country"
//                   name="country"
//                   options={dropDownOptions}
//                 />

//                 <FormikControl
//                   control="select"
//                   label="State"
//                   name="state"
//                   options={dropDownOptions2}
//                 />

//                 <button
//                   type="submit"
//                   className={`${isFormSubmitted ? "bg-gray-300 cursor-not-allowed" : "bg-enabled-btn"
//                     } text-slate-50 text-base font-medium border rounded-md cursor-pointer my-8 py-2 px-20`}
//                   disabled={!formik.isValid || isFormSubmitted}
//                 >
//                   {isFormSubmitted ? "Saving..." : "Save"}
//                 </button>
//               </Form>
//             )}
//           </Formik>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default CompanyProfileTab;




// import { useState, useRef } from "react";
// import { Formik, Form } from "formik";
// import * as Yup from "yup";
// import axios from "axios";
// import Toast from "./Toast";
// import { CompanyLogo, DropAndDragIcon } from "../../../components/common/IconComponents";
// import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";

// const CompanyProfileTab = () => {
//   const [dragActive, setDragActive] = useState(false);
//   const [uploadedImage, setUploadedImage] = useState(null);
//   const inputRef = useRef(null);

//   const handleDrag = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     if (e.type === "dragenter" || e.type === "dragover") {
//       setDragActive(true);
//     } else if (e.type === "dragleave") {
//       setDragActive(false);
//     }
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setDragActive(false);
//     if (e.dataTransfer.files && e.dataTransfer.files[0]) {
//       handleFile(e.dataTransfer.files);
//     }
//   };

//   const handleChange = (e) => {
//     e.preventDefault();
//     if (e.target.files && e.target.files[0]) {
//       handleFile(e.target.files);
//     }
//   };

//   const initialValues = {
//     companyName: "",
//     companyEmail: "idowu.oluwatofunmi9@gmail.com",
//     numberOfEmployees: "",
//     country: "",
//     state: "",
//   };

//   const dropDownOptions = [
//     { key: "Select", value: "" },
//     { key: "Nigeria", value: "option1" },
//     { key: "Canada", value: "option2" },
//     { key: "USA", value: "option3" },
//     { key: "UK", value: "option4" },
//     { key: "UAE", value: "option5" },
//   ];

//   const dropDownOptions2 = [
//     { key: "Select", value: "" },
//     { key: "Lagos", value: "option1" },
//     { key: "Abuja", value: "option2" },
//     { key: "Port Harcourt", value: "option3" },
//     { key: "Ogun", value: "option4" },
//     { key: "Delta", value: "option5" },
//   ];

//   const validationSchema = Yup.object({
//     companyName: Yup.string().required("Required"),
//     companyEmail: Yup.string(),
//     numberOfEmployees: Yup.string().required("Required"),
//     country: Yup.string().required("Required"),
//     state: Yup.string().required("Required"),
//   });

//   const [showToast, setShowToast] = useState(false);
//   const [isFormSubmitted, setIsFormSubmitted] = useState(false);
//   const [isImageUploaded, setIsImageUploaded] = useState(false);

//   const toggleToast = () => {
//     setShowToast(!showToast);
//   };

//   const handleFile = (files) => {
//     const file = files[0];
//     const formData = new FormData();
//     formData.append("file", file);
//     formData.append("upload_preset", "eqprvodt");

//     axios
//       .post("https://api.cloudinary.com/v1_1/darnus3nl/image/upload", formData)
//       .then((response) => {
//         const imageUrl = response.data.secure_url;
//         setUploadedImage(imageUrl);
//         setIsImageUploaded(true);
//       })
//       .catch((error) => {
//         console.error("Error uploading image to Cloudinary: ", error);
//       });
//   };

//   const onSubmit = (values, { resetForm }) => {
//     console.log("Form data", values);
//     toggleToast();
//     setIsFormSubmitted(true);
//     resetForm();
//   };

//   return (
//     <section>
//       <div className="md:px-4">
//         {showToast && (
//           <Toast
//             message="Your company’s profile has been successfully updated"
//             onClose={toggleToast}
//           />
//         )}

//         <nav className="md:w-[662px] sticky md:fixed z-20 top-16 md:top-0 bg-white">
//           <h1 className="mt-4 py-2 text-xl md:text-[28px] font-medium">Company's Profile</h1>
//         </nav>

//         <div className="flex gap-8 mt-4 md:mt-24 mb-4">
//           {uploadedImage ? (
//             <img src={uploadedImage} alt="Uploaded" className="max-h-64 max-w-full" />
//           ) : (
//             <div
//               id="drag-drop-area"
//               className={`h-full border rounded bg-th-bg text-center p-4 ${dragActive ? "bg-white" : ""}`}
//               onDragEnter={handleDrag}
//               onDragOver={handleDrag}
//               onDragLeave={handleDrag}
//               onDrop={handleDrop}
//             >
//               <button
//                 className="cursor-pointer border-none p-0.5 bg-transparent"
//                 onClick={() => inputRef.current.click()}
//               >
//                 <DropAndDragIcon className="mb-1" />
//               </button>
//               <p className="mb-1 text-base font-normal">
//                 <span className="text-enabled-btn">Click to upload</span> or drag and drop
//               </p>
//               <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
//             </div>
//           )}
//           <input
//             ref={inputRef}
//             type="file"
//             id="input-file-upload"
//             multiple={false}
//             onChange={handleChange}
//             className="hidden"
//           />
//         </div>

//         <div>
//           <Formik
//             initialValues={initialValues}
//             validationSchema={validationSchema}
//             onSubmit={onSubmit}
//           >
//             {(formik) => (
//               <Form className="text-activesettings-text text-base font-normal">
//                 <FormikControl
//                   control="input"
//                   label="Company's name"
//                   type="text"
//                   placeholder="Flora"
//                   name="companyName"
//                 />

//                 <FormikControl
//                   control="input"
//                   label="Company's email address"
//                   type="email"
//                   placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
//                   name="companyEmail"
//                   disabled
//                 />

//                 <FormikControl
//                   control="input"
//                   label="Number of employees"
//                   type="text"
//                   placeholder="e.g. 50"
//                   name="numberOfEmployees"
//                 />

//                 <FormikControl
//                   control="select"
//                   label="Country"
//                   name="country"
//                   options={dropDownOptions}
//                 />

//                 <FormikControl
//                   control="select"
//                   label="State"
//                   name="state"
//                   options={dropDownOptions2}
//                 />

//                 {isImageUploaded && formik.isValid && !isFormSubmitted && (
//                   <div className="text-center mt-4 mb-8">
//                     <button
//                       type="submit"
//                       className="bg-enabled-btn text-slate-50 text-base font-medium border rounded-md cursor-pointer py-2 px-4"
//                     >
//                       Save Changes
//                     </button>
//                   </div>
//                 )}
//               </Form>
//             )}
//           </Formik>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default CompanyProfileTab;



// click to upload works, button disappears.
// import { useState, useRef } from "react";
// import { Formik, Form } from "formik";
// import * as Yup from "yup";
// import axios from "axios";
// import Toast from "./Toast";
// import { CloseToastIcon, CompanyLogo, DropAndDragIcon, GoodToastIcon } from "../../../components/common/IconComponents";
// import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";


// const CompanyProfileTab = () => {
//   const [dragActive, setDragActive] = useState(false);
//   const [uploadedImage, setUploadedImage] = useState(null);
//   const inputRef = useRef(null);
//   const [isImageUploaded, setIsImageUploaded] = useState(false);
//   const [isFormSubmitted, setIsFormSubmitted] = useState(false);
//   const [showToast, setShowToast] = useState(false);

//   const handleDrag = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     if (e.type === "dragenter" || e.type === "dragover") {
//       setDragActive(true);
//     } else if (e.type === "dragleave") {
//       setDragActive(false);
//     }
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setDragActive(false);
//     if (e.dataTransfer.files && e.dataTransfer.files[0]) {
//       handleFile(e.dataTransfer.files);
//     }
//   };

//   const handleChange = (e) => {
//     e.preventDefault();
//     if (e.target.files && e.target.files[0]) {
//       handleFile(e.target.files);
//     }
//   };

//   const initialValues = {
//     companyName: "",
//     companyEmail: "idowu.oluwatofunmi9@gmail.com",
//     numberOfEmployees: "",
//     country: "",
//     state: "",
//   };

//   const dropDownOptions = [
//     { key: "Select", value: "" },
//     { key: "Nigeria", value: "option1" },
//     { key: "Canada", value: "option2" },
//     { key: "USA", value: "option3" },
//     { key: "UK", value: "option4" },
//     { key: "UAE", value: "option5" },
//   ];

//   const dropDownOptions2 = [
//     { key: "Select", value: "" },
//     { key: "Lagos", value: "option1" },
//     { key: "Abuja", value: "option2" },
//     { key: "Port Harcourt", value: "option3" },
//     { key: "Ogun", value: "option4" },
//     { key: "Delta", value: "option5" },
//   ];

//   const validationSchema = Yup.object({
//     companyName: Yup.string().required("Required"),
//     companyEmail: Yup.string(),
//     numberOfEmployees: Yup.string().required("Required"),
//     country: Yup.string().required("Required"),
//     state: Yup.string().required("Required"),
//   });

//   const toggleToast = () => {
//     setShowToast(!showToast);
//   };

//   const handleFile = (files) => {
//     const file = files[0];
//     const formData = new FormData();
//     formData.append("file", file);
//     formData.append("upload_preset", "eqprvodt");

//     axios
//       .post("https://api.cloudinary.com/v1_1/darnus3nl/image/upload", formData)
//       .then((response) => {
//         const imageUrl = response.data.secure_url;
//         setUploadedImage(imageUrl);
//         setIsImageUploaded(true);
//       })
//       .catch((error) => {
//         console.error("Error uploading image to Cloudinary: ", error);
//       });
//   };

//   const onSubmit = (values) => {
//     console.log("Form data", values);
//     setIsFormSubmitted(true);
//     toggleToast();
//   };

//   return (
//     <section>
//       <div className="md:px-4">
//         {showToast && isFormSubmitted && isImageUploaded && (
//           <Toast
//             message="Your company’s profile has been successfully updated"
//             successIcon={<GoodToastIcon className="w-4 h-4 lg:w-8 lg:h-8" />}
//             closeIcon={<CloseToastIcon />}
//             onClose={toggleToast}
//           />
//         )}

//         <nav className="md:w-[662px] sticky md:fixed z-20 top-16 md:top-0 bg-white">
//           <h1 className="mt-4 py-2 text-xl md:text-[28px] font-medium">Company's Profile</h1>
//         </nav>

//         <div className="flex gap-8 mt-4 md:mt-24 mb-4">
//           <span className="">
//             <CompanyLogo />
//           </span>

//           <div
//             id="drag-drop-area"
//             className={`flex items-center justify-center h-full border rounded bg-th-bg text-center p-4 ${dragActive ? "bg-white" : ""}`}
//             onDragEnter={handleDrag}
//             onDragOver={handleDrag}
//             onDragLeave={handleDrag}
//             onDrop={handleDrop}
//           >
//             {uploadedImage ? (
//               <img src={uploadedImage} alt="Uploaded" className="max-h-64 max-w-full" />
//             ) : (
//               <div>
//                 <button
//                   className="cursor-pointer border-none p-0.5 bg-transparent"
//                   onClick={() => inputRef.current.click()}
//                 >
//                   <DropAndDragIcon className="mb-1" />
//                 </button>
//                 <p className="mb-1 text-base font-normal">
//                   <span className="text-enabled-btn">Click to upload</span> or drag and drop
//                 </p>
//                 <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
//               </div>
//             )}
//           </div>
//           <input
//             ref={inputRef}
//             type="file"
//             id="input-file-upload"
//             multiple={false}
//             onChange={handleChange}
//             className="hidden"
//           />
//         </div>

//         <div className="">
//           <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
//             {(formik) => {
//               return (
//                 <Form className="text-activesettings-text text-base font-normal">
//                   {/* Form inputs */}
//                   <FormikControl control="input" label="Company's name" type="text" placeholder="Flora" name="companyName" />
//                   <FormikControl
//                     className="w-full px-4 py-2 pr-10 text-disabled text-xs md:text-sm border border-disabled rounded-md focus:outline-none focus:ring-2 bg-neutral-bg shadow-md"
//                     control="input"
//                     label="Company's email address "
//                     type="email"
//                     placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
//                     name="email"
//                     disabled={true}
//                     value="idowu.oluwatofunmi9@gmail.com"
//                   />
//                   <FormikControl
//                     control="input"
//                     label="Number of employees"
//                     type="text"
//                     placeholder="e.g. 50"
//                     name="numberOfEmployees"
//                     onChange={formik.handleChange}
//                   />
//                   <FormikControl control="select" label="Country" name="country" options={dropDownOptions} />
//                   <FormikControl control="select" label="State" name="state" options={dropDownOptions2} />

//                   {/* Submit button */}
//                   <div className="text-center mt-4 mb-8">
//                     <button
//                       type="submit"
//                       disabled={!formik.isValid}
//                       className={`${formik.isValid
//                           ? "bg-activesettings-btn text-white"
//                           : "bg-disabled-btn text-disabled-text cursor-not-allowed"
//                         } text-base font-normal py-2 px-8 rounded-lg`}
//                     >
//                       Update
//                     </button>
//                   </div>
//                 </Form>
//               );
//             }}
//           </Formik>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default CompanyProfileTab;


// recent - okay without drag and drop
// import { useState, useRef } from "react";
// // import { useNavigate } from "react-router-dom";
// import { Formik, Form } from "formik";
// import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";
// import * as Yup from "yup";
// import { CompanyLogo, GoodToastIcon, CloseToastIcon, DropAndDragIcon } from "../../../components/common/IconComponents";
// import axios from "axios";
// import Toast from "./Toast";

// const CompanyProfileTab = () => {
//   // const navigate = useNavigate();

//   const [dragActive, setDragActive] = useState(false);
//   const [uploadedImage, setUploadedImage] = useState(null);
//   const inputRef = useRef(null);

//   const handleDrag = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     if (e.type === "dragenter" || e.type === "dragover") {
//       setDragActive(true);
//     } else if (e.type === "dragleave") {
//       setDragActive(false);
//     }
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setDragActive(false);
//     if (e.dataTransfer.files && e.dataTransfer.files[0]) {
//       handleFile(e.dataTransfer.files);
//     }
//   };

//   const handleChange = (e) => {
//     e.preventDefault();
//     if (e.target.files && e.target.files[0]) {
//       handleFile(e.target.files);
//     }
//   };

//   const initialValues = {
//     companyName: "",
//     companyEmail: "idowu.oluwatofunmi9@gmail.com",
//     numberOfEmployees: "",
//     country: "",
//     state: "",
//   };

//   const dropDownOptions = [
//     { key: "Select", value: "" },
//     { key: "Nigeria", value: "option1" },
//     { key: "Canada", value: "option2" },
//     { key: "USA", value: "option3" },
//     { key: "UK", value: "option4" },
//     { key: "UAE", value: "option5" },
//   ];

//   // Settings company's profile
//   const dropDownOptions2 = [
//     { key: "Select", value: "" },
//     { key: "Lagos", value: "option1" },
//     { key: "Abuja", value: "option2" },
//     { key: "Port Harcourt", value: "option3" },
//     { key: "Ogun", value: "option4" },
//     { key: "Delta", value: "option5" },
//   ];

//   // validation
//   const validationSchema = Yup.object({
//     companyName: Yup.string().required("Required"),
//     companyEmail: Yup.string(),
//     numberOfEmployees: Yup.string().required("Required"),
//     contry: Yup.string().required("Required"),
//     state: Yup.string().required("Required"),
//   });

//   // Toast state and toggle function
//   const [showToast, setShowToast] = useState(false);

//   const toggleToast = () => {
//     setShowToast(!showToast);
//   };

//   // Form submission
//   const onSubmit = (values) => {
//     console.log("Form data", values);
//     toggleToast(); // Show the toast
//   };

//   const handleFile = (files) => {
//     const file = files[0];

//     // Create a FormData object
//     const formData = new FormData();
//     formData.append("file", file);
//     formData.append("upload_preset", "eqprvodt");


//     // Make an HTTP request to Cloudinary using Axios
//     axios
//       .post("https://api.cloudinary.com/v1_1/darnus3nl/image/upload", formData)
//       .then((response) => {
//         // Handling response from Cloudinary
//         console.log(response.data);
//         // Extracting image URL from the response
//         const imageUrl = response.data.secure_url;
//         setUploadedImage(imageUrl);
//       })
//       .catch((error) => {
//         console.error("Error uploading image to Cloudinary: ", error);
//       });
//   };

//   return (
//     <section>
//       <div className="md:px-4">
//         {/* Toast */}
//         {showToast && (
//           <Toast
//             message="Your company’s profile has been successfully updated"
//             successIcon={<GoodToastIcon className="w-4 h-4 lg:w-8 lg:h-8" />}
//             closeIcon={<CloseToastIcon />}
//             onClose={toggleToast}
//           />
//         )}

//         <nav className="md:w-[662px] sticky md:fixed z-20 top-16 md:top-0 bg-white">
//           <h1 className="mt-4 py-2 text-xl md:text-[28px] font-medium">Company's Profile</h1>
//         </nav>

//         <div className="flex gap-8 mt-4 md:mt-24 mb-4">
//           <span className="">
//             <CompanyLogo />
//           </span>

//           <div
//             id="drag-drop-area"
//             className={`flex items-center justify-center h-full border rounded bg-th-bg text-center p-4 ${dragActive ? "bg-white" : ""
//               }`}
//             onDragEnter={handleDrag}
//             onDragOver={handleDrag}
//             onDragLeave={handleDrag}
//             onDrop={handleDrop}
//           >
//             {uploadedImage ? (
//               <img src={uploadedImage} alt="Uploaded" className="max-h-64 max-w-full" />
//             ) : (
//               <div>
//                 <button
//                   className="cursor-pointer border-none p-0.5 bg-transparent"
//                   onClick={() => inputRef.current.click()}
//                 >
//                   <DropAndDragIcon className="mb-1" />
//                 </button>
//                 <p className="mb-1 text-base font-normal">
//                   <span className="text-enabled-btn">Click to upload</span> or drag and drop
//                 </p>
//                 <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
//               </div>
//             )}
//           </div>
//           <input
//             ref={inputRef}
//             type="file"
//             id="input-file-upload"
//             multiple={false}
//             onChange={handleChange}
//             className="hidden"
//           />
//         </div>

//         <div className="">
//           <Formik
//             initialValues={initialValues}
//             validationSchema={validationSchema}
//             onSubmit={onSubmit}
//           >
//             {(formik) => {
//               return (
//                 <Form className="text-activesettings-text text-base font-normal">
//                   <FormikControl
//                     control="input"
//                     label="Company's name"
//                     type="text"
//                     placeholder="Flora"
//                     name="companyName"
//                   />

//                   <FormikControl
//                     className="w-full px-4 py-2 pr-10 text-disabled text-xs md:text-sm border border-disabled rounded-md focus:outline-none focus:ring-2 bg-neutral-bg shadow-md"
//                     control="input"
//                     label="Company's email address "
//                     type="email"
//                     placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
//                     name="email"
//                     disabled={true}
//                     value="idowu.oluwatofunmi9@gmail.com"

//                   />

//                   <FormikControl
//                     control="input"
//                     label="Number of employees"
//                     type="text"
//                     placeholder="e.g. 50"
//                     name="numberOfEmployees"
//                     onChange={formik.handleChange}
//                   />

//                   <FormikControl
//                     control="select"
//                     label="Country"
//                     name="country"
//                     options={dropDownOptions}
//                   />

//                   <FormikControl
//                     control="select"
//                     label="State"
//                     name="state"
//                     options={dropDownOptions2}
//                   />

//                   <button
//                     type="submit"
//                     className={
//                       "bg-enabled-btn text-slate-50 text-base font-medium border rounded-md cursor-pointer my-8 py-2 px-20"
//                     }
//                     disabled={!formik.isValid}
//                   >
//                     Save
//                   </button>
//                 </Form>
//               );
//             }}
//           </Formik>
//         </div>
//       </div>
//     </section>
//   );
// }

// export default CompanyProfileTab;




// okay without drop and drag
// import { useState, useRef } from "react";
// // import { useNavigate } from "react-router-dom";
// import { Formik, Form } from "formik";
// import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";
// import * as Yup from "yup";
// import { CompanyLogo, DropAndDragIcon, GoodToastIcon, CloseToastIcon } from "../../../components/common/IconComponents";
// import axios from "axios";
// import Toast from "./Toast";

// const CompanyProfileTab = () => {
//   // const navigate = useNavigate();

//   const [dragActive, setDragActive] = useState(false);
//   const [uploadedImage, setUploadedImage] = useState(null);
//   const inputRef = useRef(null);

//   const handleDrag = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     if (e.type === "dragenter" || e.type === "dragover") {
//       setDragActive(true);
//     } else if (e.type === "dragleave") {
//       setDragActive(false);
//     }
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setDragActive(false);
//     if (e.dataTransfer.files && e.dataTransfer.files[0]) {
//       handleFile(e.dataTransfer.files);
//     }
//   };

//   const handleChange = (e) => {
//     e.preventDefault();
//     if (e.target.files && e.target.files[0]) {
//       handleFile(e.target.files);
//     }
//   };

//   const initialValues = {
//     companyName: "",
//     companyEmail: "idowu.oluwatofunmi9@gmail.com",
//     numberOfEmployees: "",
//     country: "",
//     state: "",
//   };

//   const dropDownOptions = [
//     { key: "Select", value: "" },
//     { key: "Nigeria", value: "option1" },
//     { key: "Canada", value: "option2" },
//     { key: "USA", value: "option3" },
//     { key: "UK", value: "option4" },
//     { key: "UAE", value: "option5" },
//   ];

//   // Settings company's profile
//   const dropDownOptions2 = [
//     { key: "Select", value: "" },
//     { key: "Lagos", value: "option1" },
//     { key: "Abuja", value: "option2" },
//     { key: "Port Harcourt", value: "option3" },
//     { key: "Ogun", value: "option4" },
//     { key: "Delta", value: "option5" },
//   ];

//   // validation
//   const validationSchema = Yup.object({
//     companyName: Yup.string().required("Required"),
//     companyEmail: Yup.string(),
//     numberOfEmployees: Yup.string().required("Required"),
//     contry: Yup.string().required("Required"),
//     state: Yup.string().required("Required"),
//   });

//   // Toast state and toggle function
//   const [showToast, setShowToast] = useState(false);

//   const toggleToast = () => {
//     setShowToast(!showToast);
//   };


//   //   Form submission
//   const onSubmit = (values) => {
//     console.log("Form data", values);
//     // navigate("/");
//     toggleToast(); // Show the toast
//     // setShowToast(true); // Show toast when form is submitted
//   };

//   const handleFile = (files) => {
//     const file = files[0];

//     // Create a FormData object
//     const formData = new FormData();
//     formData.append("file", file);
//     formData.append("upload_preset", "eqprvodt");


//     // Make an HTTP request to Cloudinary using Axios
//     axios
//       .post("https://api.cloudinary.com/v1_1/darnus3nl/image/upload", formData)
//       .then((response) => {
//         // Handling response from Cloudinary
//         console.log(response.data);
//         // Extracting image URL from the response
//         const imageUrl = response.data.secure_url;
//         setUploadedImage(imageUrl);
//       })
//       .catch((error) => {
//         console.error("Error uploading image to Cloudinary: ", error);
//       });
//   };

//   return (
//     <section> 
//       <div className="md:px-4">
//         {/* Toast */}
//         {showToast && (
//           <Toast
//             message="Your company’s profile has been successfully updated"
//             successIcon={<GoodToastIcon />}
//             closeIcon={<CloseToastIcon />}
//             onClose={toggleToast}
//           />
//         )}

//         <nav className="md:w-[662px] sticky md:fixed z-20 top-16 md:top-0 bg-white">
//           <h1 className="mt-4 py-2 text-xl md:text-[28px] font-medium">Company's Profile</h1>
//         </nav>

//         <div className="flex gap-8 mt-4 md:mt-24 mb-4">
//           <span className="">
//             <CompanyLogo />
//           </span>

//           <form
//             id="form-file-upload"
//             onDragEnter={handleDrag}
//             onSubmit={(e) => e.preventDefault()}
//             className=""
//           >
//             <input
//               ref={inputRef}
//               type="file"
//               id="input-file-upload"
//               multiple={false}
//               onChange={handleChange}
//               className="hidden"
//             />
//             <label
//               id="label-file-upload"
//               htmlFor="input-file-upload"
//               className={`flex items-center justify-center h-full border rounded bg-th-bg text-center p-4 ${dragActive ? "bg-white" : ""
//                 }`}
//             >
//               {uploadedImage ? (
//                 <img
//                   src={uploadedImage}
//                   alt="Uploaded"
//                   className="max-h-64 max-w-full"
//                 />
//               ) : (
//                 <div>
//                   <button
//                     className="cursor-pointer border-none p-0.5 bg-transparent"
//                     onClick={() => inputRef.current.click()}
//                     >
//                     <DropAndDragIcon className="mb-1" />
//                   </button>
//                     <p className="mb-1 text-base font-normal"><span className="text-enabled-btn">Click to upload</span> or drag and drop</p>
//                     <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
//                 </div>
//               )}
//             </label>
//             {dragActive && (
//               <div
//                 className="absolute w-full h-full rounded top-0 right-0 left-0 bottom-0"
//                 onDragEnter={handleDrag}
//                 onDragLeave={handleDrag}
//                 onDragOver={handleDrag}
//                 onDrop={handleDrop}
//               ></div>
//             )}
//           </form>
//         </div>

//         <div className="">
//           <Formik
//             initialValues={initialValues}
//             validationSchema={validationSchema}
//             onSubmit={onSubmit}
//           >
//             {(formik) => {
//               return (
//                 <Form className="text-activesettings-text text-base font-normal">
//                   <FormikControl
//                     control="input"
//                     label="Company's name"
//                     type="text"
//                     placeholder="Flora"
//                     name="companyName"
//                   />

//                   <FormikControl
//                     className="w-full px-4 py-2 pr-10 text-disabled text-xs md:text-sm border border-disabled rounded-md focus:outline-none focus:ring-2 bg-neutral-bg shadow-md"
//                     control="input"
//                     label="Company's email address "
//                     type="email"
//                     placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
//                     name="email"
//                     disabled={true} 
//                     value="idowu.oluwatofunmi9@gmail.com"
                   
//                   />

//                   <FormikControl
//                     control="input"
//                     label="Number of employees"
//                     type="text"
//                     placeholder="e.g. 50"
//                     name="numberOfEmployees"
//                     onChange={formik.handleChange} 
//                   />

//                   <FormikControl
//                     control="select"
//                     label="Country"
//                     name="country"
//                     options={dropDownOptions}
//                   />

//                   <FormikControl
//                     control="select"
//                     label="State"
//                     name="state"
//                     options={dropDownOptions2}
//                   />

//                   <button
//                     type="submit"
//                     className={
//                       "bg-enabled-btn text-slate-50 text-base font-medium border rounded-md cursor-pointer my-8 py-2 px-20"
//                     }
//                     disabled={!formik.isValid}
//                     onClick={toggleToast} // Call toggleToast on button click
//                   >
//                     Save
//                   </button>
//                 </Form>
//               );
//             }}
//           </Formik>
//         </div>
//       </div>
//     </section>
//   );
// }

// export default CompanyProfileTab;





// fall back drag and drop not functional
// // import { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { Formik, Form } from "formik";
// import FormikControl from "../../../components/signUpForm/ReusableFormik/FormikControl";
// import * as Yup from "yup";
// import { CompanyLogo, DropAndDragIcon } from "../../../components/common/IconComponents";

// const CompanyProfileTab = () => {
//   const navigate = useNavigate();

//   const initialValues = {
//     email: "",
//     companyName: "",
//     numberOfEmployees: "",
//     country: "",
//     state: "",
//   };

//   const dropDownOptions = [
//     { key: "Select", value: "" },
//     { key: "Nigeria", value: "option1" },
//     { key: "Canada", value: "option2" },
//     { key: "USA", value: "option3" },
//     { key: "UK", value: "option4" },
//     { key: "UAE", value: "option5" },
//   ];

//   // Settings company's profile
//   const dropDownOptions2 = [
//     { key: "Select", value: "" },
//     { key: "Lagos", value: "option1" },
//     { key: "Abuja", value: "option2" },
//     { key: "Port Harcourt", value: "option3" },
//     { key: "Ogun", value: "option4" },
//     { key: "Delta", value: "option5" },
//   ];
  
//   // validation
//   const validationSchema = Yup.object({
//     email: Yup.string().email("Invalid email format").required("Required"),
//     companyName: Yup.string().required("Required"),
//     numberOfEmployees: Yup.string().required("Required"),
//     contry: Yup.string().required("Required"),
//     state: Yup.string().required("Required"),
//   });

//   //   Form submission
//   const onSubmit = (values) => {
//     console.log("Form data", values);
//     navigate("/");
//   };

//   return (
//     <section>
//       <div className="px-4">
//         <h1 className="mt-4 text-[28px] font-medium">Company's Profile</h1>

//         <div className="flex gap-8 mt-4 mb-4">
//           <span className="">
//             <CompanyLogo />
//           </span>

//           <div className="flex flex-col items-center bg-chat-gray-bg p-4">
//             <DropAndDragIcon className="mb-1"/>
//             <p className="mb-1 text-base font-normal"><span className="text-enabled-btn">Click to upload</span> or drag and drop</p>
//             <p className="text-success-email text-sm font-normal">SVG, PNG, or JPG (max. 800x400px)</p>
//           </div>
//         </div>

//         <div className="">
//           <Formik
//             initialValues={initialValues}
//             validationSchema={validationSchema}
//             onSubmit={onSubmit}
//           >
//             {(formik) => {
//               return (
//                 <Form className="text-activesettings-text text-base font-normal">
//                   <FormikControl
//                     control="input"
//                     label="Company's name"
//                     type="text"
//                     placeholder="Flora"
//                     name="companyName"
//                   />

//                   <FormikControl
//                     control="input"
//                     label="Company's email address "
//                     type="email"
//                     placeholder="e.g. idowu.oluwatofunmi9@gmail.com"
//                     name="email"
//                   />

//                   <FormikControl
//                     control="input"
//                     label="Number of employees"
//                     type="text"
//                     placeholder="e.g. 50"
//                     name="numberOfEmployee"
//                   />

//                   <FormikControl 
//                     control="select"
//                     label="Country"
//                     name="conutry"
//                     options={dropDownOptions}
//                   />

//                   <FormikControl
//                     control="select"
//                     label="State"
//                     name="state"
//                     options={dropDownOptions2}
//                   />

//                   <button
//                     type="submit"
//                     className={
//                       "bg-enabled-btn text-slate-50 text-base font-medium border rounded-md cursor-pointer my-8 py-2 px-20"
//                     }
//                     disabled={!formik.isValid}
//                   >
//                     Save
//                   </button>
//                 </Form>
//               );
//             }}
//           </Formik>  
//         </div>
//       </div>
//     </section>
//   );
// }

// export default CompanyProfileTab